import { useAuth } from "@clerk/clerk-react";
import { useCallback } from "react";
import type { WaitlistOrder } from "@/utils/orderSpySort";
// Same project the rest of the app talks to.
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/lib/supabaseClient";
import { countOrdersFromOrderNo } from "../../supabase/functions/waitlist-orders/lib";

export interface WaitlistSummary {
  order_count: number;
  earliest_promise_time: string | null;
  latest_promise_time: string | null;
}

export interface WaitlistResult {
  orders: WaitlistOrder[];
  summary: WaitlistSummary;
}

/**
 * Returns a function that fetches one store's live waitlist via the
 * waitlist-orders edge function. Operator-initiated only: there is no polling
 * or auto-refresh (spec 7).
 *
 * Cache busting (spec 4.5, Critical): cache:'no-store' plus a unique ?_=<ts>
 * query param so neither the HTTP cache nor the back/forward cache can answer
 * with a previous fetch's orders. This is the belt-and-braces other half of the
 * server's no-store headers; both are needed.
 */
export const useWaitlistFetcher = () => {
  const { getToken } = useAuth();

  return useCallback(
    async (storeId: string): Promise<WaitlistResult> => {
      const jwt = await getToken({ template: "supabase" });
      if (!jwt) throw new Error("Not authenticated. Sign in and try again.");

      const url =
        `${SUPABASE_URL}/functions/v1/waitlist-orders` +
        `?store_id=${encodeURIComponent(storeId)}&_=${Date.now()}`;

      const res = await fetch(url, {
        method: "GET",
        cache: "no-store",
        headers: {
          Authorization: `Bearer ${jwt}`,
          apikey: SUPABASE_PUBLISHABLE_KEY,
        },
      });

      // The function returns JSON on every path, so surface the server's own
      // error string rather than a bare status code (spec 3.4 / 5).
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        throw new Error(`Request failed (${res.status}). The response was not JSON.`);
      }

      const data = body as Partial<WaitlistResult> & { error?: string };
      if (!res.ok) {
        throw new Error(data?.error || `Request failed (${res.status}).`);
      }
      return {
        orders: data.orders ?? [],
        summary:
          data.summary ?? {
            order_count: countOrdersFromOrderNo(data.orders ?? []),
            earliest_promise_time: null,
            latest_promise_time: null,
          },
      };
    },
    [getToken],
  );
};
